import { CommonModule } from '@angular/common';
import { Component, Input, inject } from '@angular/core';

import { I18nService } from '../../../core/services/i18n.service';

export interface HorizontalStepperStep {
  title: string;
  description: string;
}

@Component({
  selector: 'umkt-horizontal-stepper',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './horizontal-stepper.component.html',
  styleUrls: ['./horizontal-stepper.component.scss'],
})
export class HorizontalStepperComponent {
  private readonly i18n = inject(I18nService);

  @Input() steps: HorizontalStepperStep[] = [];
  @Input() currentStepIndex = 0;
  @Input() ariaLabel = 'Step flow';
  @Input() title = '';
  @Input() subtitle = '';
  @Input() titleKey = '';
  @Input() subtitleKey = '';
  @Input() showTitle = true;
  @Input() showSubtitle = true;
  @Input() stepsTranslationPrefix = '';
  @Input() stepsCount = 0;

  get resolvedSteps(): HorizontalStepperStep[] {
    if (this.stepsTranslationPrefix.trim() && this.stepsCount > 0) {
      return Array.from({ length: this.stepsCount }, (_, index) => {
        const position = index + 1;
        return {
          title: this.i18n.instant(
            `${this.stepsTranslationPrefix}.step${position}.title`
          ),
          description: this.i18n.instant(
            `${this.stepsTranslationPrefix}.step${position}.description`
          ),
        };
      });
    }

    return this.steps;
  }

  get resolvedTitle(): string {
    return this.titleKey.trim() ? this.i18n.instant(this.titleKey) : this.title;
  }

  get resolvedSubtitle(): string {
    return this.subtitleKey.trim()
      ? this.i18n.instant(this.subtitleKey)
      : this.subtitle;
  }

  get shouldRenderTitle(): boolean {
    return this.showTitle && this.resolvedTitle.trim().length > 0;
  }

  get shouldRenderSubtitle(): boolean {
    return this.showSubtitle && this.resolvedSubtitle.trim().length > 0;
  }

  get hasFullHeader(): boolean {
    return this.shouldRenderTitle && this.shouldRenderSubtitle;
  }

  get isStepsOnly(): boolean {
    return !this.shouldRenderTitle && !this.shouldRenderSubtitle;
  }

  get safeCurrentStepIndex(): number {
    if (!this.resolvedSteps.length) return -1;
    if (this.currentStepIndex < 0) return -1;
    const clampedIndex = Math.min(
      Math.max(this.currentStepIndex ?? 0, 0),
      this.resolvedSteps.length - 1
    );
    return clampedIndex;
  }

  isCompleted(index: number): boolean {
    return index < this.safeCurrentStepIndex;
  }

  isActive(index: number): boolean {
    return index === this.safeCurrentStepIndex;
  }

  trackByStepIndex(index: number): number {
    return index;
  }
}

